<script setup lang="ts">
import PageHeader from '@/components/Globals/PageHeader.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import ListingFilters from '@/components/Globals/ListingFilters.vue'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import CompanyShiftModal from './modals/CompanyShiftModal.vue'
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import IcDeleteClose from '@/components/Icons/IcDeleteClose.vue'
import { useSuccessStore } from '@/store/success'
import ShiftResource from '@/api/resources/shift'
import { useCompanyStore } from '@/store/company'
import { IShift } from '@/interfaces/entities/shifts'
import { computed, onMounted, ref } from 'vue'
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { formatOutsideRegularUtcTimeToLocalHHmm } from '@/utils/date/date-time'
import { useI18n } from '@/composable/useI18n'

const isLoading = ref(true)
const companyStore = useCompanyStore()
const shifts = computed(() => companyStore.shifts)
const searchQuery = ref('')
const { t } = useI18n()

const isModalOpen = ref(false)
const selectedShiftId = ref<number | null>(null)

const isRemoveModalOpen = ref(false)
const selectedShiftForDelete = ref<IShift | null>(null)
const isDeleting = ref(false)

function openCreateModal() {
  selectedShiftId.value = null
  isModalOpen.value = true
}

function openEditModal(id: number) {
  selectedShiftId.value = id
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
  selectedShiftId.value = null
}

function openRemoveModal(shift: IShift) {
  selectedShiftForDelete.value = shift
  isRemoveModalOpen.value = true
}

function closeRemoveModal() {
  isRemoveModalOpen.value = false
  selectedShiftForDelete.value = null
  isDeleting.value = false
}

async function deleteShift() {
  if (!selectedShiftForDelete.value) return
  
  isDeleting.value = true
  try {
    await ShiftResource.delete(selectedShiftForDelete.value.id)
    useSuccessStore().setSuccess([t('company_shifts.success.removed')])
    handleRefresh()
  } catch (error) {
    console.error('Failed to delete shift:', error)
  } finally {
    closeRemoveModal()
  }
}

const filteredShifts = computed(() => {
  let result = shifts.value

  const q = searchQuery.value.trim()
  if (q) {
    result = result.filter((s) => matchesSearchAccentInsensitive(s.name, q))
  }

  return result
})

async function fetchShifts() {
  try {
    await companyStore.fetchCompanyShifts()
  } catch (error) {
    console.error('Erro ao buscar turnos', error)
  }
}

onMounted(() => {
  fetchShifts().then(() => {
    isLoading.value = false
  })
})

function formatTime(timeStr?: string) {
  if (!timeStr) return ''
  return formatOutsideRegularUtcTimeToLocalHHmm(timeStr) || timeStr
}

function handleRefresh() {
  isLoading.value = true
  fetchShifts().then(() => {
    isLoading.value = false
  })
}
</script>

<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />

    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'dashboard' }"
        :title="t('company_shifts.title')"
        :subtitle="t('company_shifts.subtitle')">
        <template #actions>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="flex items-center justify-center border border-stroke p-2 text-slate-700 transition hover:bg-slate-50"
              :title="t('company_shifts.actions.refresh')"
              @click="handleRefresh">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <ListingFilters>
              <template #default="{ close }">
                <input
                  v-model="searchQuery"
                  type="text"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  :placeholder="t('company_shifts.search_placeholder')"
                  @keydown.enter="close" />
              </template>
            </ListingFilters>
            <button
              type="button"
              class="border border-primary bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
              @click="openCreateModal">
              {{ t('company_shifts.actions.add') }}
            </button>
          </div>
        </template>
      </PageHeader>
      
      <div class="mt-6 flex flex-col gap-4">
        <div v-for="shift in filteredShifts" :key="shift.id" class="border border-stroke bg-white p-4 shadow-sm transition hover:shadow-md">
          <div class="flex items-center justify-between gap-3">
            <h3 class="text-lg font-bold text-black-2">{{ shift.name }}</h3>
            <div class="flex shrink-0 items-center gap-3">
              <button type="button" @click="openEditModal(shift.id)" class="text-primary hover:text-primary/80" :title="t('company_shifts.actions.edit')">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
              </button>
              <button type="button" @click="openRemoveModal(shift)" class="text-red hover:text-red/80" :title="t('company_shifts.actions.delete')">
                <IcDeleteClose width="20" height="20" />
              </button>
            </div>
          </div>
          <div class="mt-2 text-sm text-slate-600">
            <p>
              {{ t('company_shifts.schedule_label') }}
              <span class="font-bold text-black-2">
                {{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }}
              </span>
            </p>
            <div v-if="shift.groups?.length" class="mt-2 flex flex-wrap items-center gap-2">
              <span class="font-semibold text-slate-500">
                {{ t('company_shifts.groups_label') }}
              </span>
              <span
                v-for="group in shift.groups"
                :key="group.id"
                class="inline-flex items-center rounded-none bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                {{ group.name }}
              </span>
            </div>
          </div>
        </div>
        
        <div v-if="filteredShifts.length === 0" class="border border-stroke bg-white py-8 text-center text-slate-500 shadow-sm">
          {{ t('company_shifts.empty') }}
        </div>
      </div>
    </div>
    
    <CompanyShiftModal
      :isOpen="isModalOpen"
      :shiftId="selectedShiftId"
      @close="closeModal"
      @saved="handleRefresh"
    />
    
    <ConfirmationModal
      :isOpen="isRemoveModalOpen"
      :cancelText="t('globals.buttons.cancel')"
      :confirmationText="t('company_shifts.actions.delete')"
      @cancel="closeRemoveModal"
      @add="deleteShift"
    >
      <template #content>
        <div class="text-center">
          <h3 class="text-lg font-medium text-black-2">{{ t('company_shifts.delete_modal.title') }}</h3>
          <p class="mt-2 text-sm text-slate-500">
            {{ t('company_shifts.delete_modal.message') }}
            <span class="font-bold">{{ selectedShiftForDelete?.name }}</span>?
            <br />
            {{ t('company_shifts.delete_modal.warning') }}
          </p>
        </div>
      </template>
    </ConfirmationModal>
  </DefaultLayout>
</template>
